import {
  BookingDates,
  BookingStatus,
  CalendarContext,
  ConfirmationDialog,
  EmployeeApi,
  getHolidayLength,
  HolidayBooking,
  isBreakpointMobile,
  OfflineButton,
  ReducerContext,
  SkeletonProps,
  translateBookingStatusToLabel,
  translateStatusToColor,
  useBreakpoint,
  useHolidayBooking,
} from '@abdoo/common';
import { Alert, Box, Grid, Skeleton } from '@mui/material';
import { format } from 'date-fns';
import React, { useCallback, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface BookingItemProps extends SkeletonProps {
  holiday: HolidayBooking;
  updateHoliday: () => void;
}

export const BookingItem = ({
  holiday,
  updateHoliday,
  isLoading,
  skeletonSx,
}: BookingItemProps) => {
  const resendRequestText = 'Resend request';
  const requestSentText = 'Request sent';
  const buttonAvailabilityText =
    'This button will be available 24 hours after the last request';
  const reminderConfirmationDialogTitle = 'Confirm reminder request';
  const [withdrawDialogOpened, setWithdrawDialogOpened] = useState<boolean>(false);
  const [cancellationDialogOpened, setCancellationDialogOpened] =
    useState<boolean>(false);
  const [resendHolidayRequestDialogOpened, setResendHolidayRequestDialogOpened] =
    useState<boolean>(false);
  const [
    resendCancellationRequestDialogOpened,
    setResendCancellationRequestDialogOpened,
  ] = useState<boolean>(false);

  const { userSettingsState } = useContext(ReducerContext);
  const { freeDays } = useContext(CalendarContext);
  const [showCanNotSendReminder, setShowCanNotSendReminder] = useState(false);

  const breakpoint = useBreakpoint();
  const isMobile = isBreakpointMobile(breakpoint);

  const location = useLocation();

  const getHolidayLengthCallback = useCallback(
    (h: HolidayBooking) => {
      return getHolidayLength(
        new Date(h.startDate),
        new Date(h.endDate),
        h.isStartHalfDay,
        h.isEndHalfDay,
        userSettingsState.workingDays,
        freeDays,
      );
    },
    [userSettingsState.workingDays, freeDays],
  );

  const {
    withdrawBooking,
    sendCancellationRequestReminder,
    sendRequestReminder,
    cancelBooking,
  } = useHolidayBooking(EmployeeApi, holiday.bookingId);

  const handleSendCancellationReminderClick = useCallback(async () => {
    if (holiday.canSendCancellationApprovalRequestReminder) {
      setResendCancellationRequestDialogOpened(true);
    } else {
      setShowCanNotSendReminder(true);
    }
  }, [holiday]);

  const handleSendReminderClick = useCallback(async () => {
    if (holiday.canSendApprovalRequestReminder) {
      setResendHolidayRequestDialogOpened(true);
    } else {
      setShowCanNotSendReminder(true);
    }
  }, [holiday]);

  const closeCanNotSendReminder = useCallback(async () => {
    setShowCanNotSendReminder(false);
  }, []);

  const onConfirmCancellation = useCallback(async () => {
    await cancelBooking(updateHoliday);
    setCancellationDialogOpened(false);
  }, [cancelBooking, updateHoliday]);

  const onRejectCancellation = useCallback(() => {
    setCancellationDialogOpened(false);
  }, []);

  const openCancellationDialog = useCallback(() => {
    setCancellationDialogOpened(true);
  }, []);

  const onConfirmResendHolidayRequest = useCallback(async () => {
    await sendRequestReminder(updateHoliday);
    setResendHolidayRequestDialogOpened(false);
  }, [sendRequestReminder, updateHoliday]);

  const onRejectResendHolidayRequest = useCallback(() => {
    setResendHolidayRequestDialogOpened(false);
  }, []);

  const onConfirmResendCancellationRequest = useCallback(async () => {
    await sendCancellationRequestReminder(updateHoliday);
    setResendCancellationRequestDialogOpened(false);
  }, [sendCancellationRequestReminder, updateHoliday]);

  const onRejectResendCancellationRequest = useCallback(() => {
    setResendCancellationRequestDialogOpened(false);
  }, []);

  const onCloseWithdrawConfirmation = useCallback(() => {
    setWithdrawDialogOpened(false);
  }, []);

  const openWithdrawConfirmation = useCallback(() => {
    setWithdrawDialogOpened(true);
  }, []);

  const onWithdrawBooking = useCallback(async () => {
    await withdrawBooking(updateHoliday);
  }, [withdrawBooking, updateHoliday]);

  const currentDate = new Date().setHours(0, 0, 0, 0);
  const isUpcoming = currentDate < new Date(holiday.endDate).setHours(0, 0, 0, 0);
  const statusStyles = {
    top: '1em',
    right: '1.25em',
    padding: '0.25em 1em',
    borderRadius: '1em',
    fontSize: '0.75em',
    backgroundColor: undefined as string | undefined,
  };

  statusStyles.backgroundColor = translateStatusToColor(
    holiday.approvalStatus,
    isUpcoming,
  );
  const label = translateBookingStatusToLabel(holiday.approvalStatusNumber, isUpcoming);
  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        height="10.25em"
        width="100%"
        sx={{ marginTop: '1rem', ...skeletonSx }}
      />
    );
  }
  return (
    <Box
      component="div"
      sx={{
        position: 'relative',
        padding: '1em',
        borderTop: '1px solid #ccc',
      }}
    >
      <Box
        component="p"
        sx={{
          fontWeight: 'bold',
          display: 'flex',
          alignItems:
            isMobile && location.pathname.includes('history') ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '0.5em',
          margin: 0,
        }}
      >
        <Box>
          <BookingDates
            start={holiday.startDate}
            end={holiday.endDate}
            isStartHalfDay={holiday.isStartHalfDay}
            isEndHalfDay={holiday.isEndHalfDay}
          />
        </Box>
        <Box component="span" sx={statusStyles}>
          {label}
        </Box>
      </Box>
      <p>Duration: {getHolidayLengthCallback(holiday)} days</p>
      <Box component="p" sx={{ fontSize: '0.75em' }}>
        Request made on {format(new Date(holiday.timeAdded), 'dd MMMM yyyy')}
      </Box>
      <Box component="section" sx={{ textAlign: 'right' }}>
        {holiday.approvalStatusNumber === BookingStatus.Pending ? (
          <Grid container justifyContent="flex-end" gap="0.5rem">
            <OfflineButton
              className={!holiday.canSendApprovalRequestReminder ? 'Mui-disabled' : ''}
              variant="contained"
              color="primary"
              onClick={handleSendReminderClick}
              data-bookingid={holiday.bookingId}
              text={
                holiday.canSendApprovalRequestReminder
                  ? resendRequestText
                  : requestSentText
              }
            />
            <OfflineButton
              variant="contained"
              color="primary"
              onClick={openWithdrawConfirmation}
              data-bookingid={holiday.bookingId}
              text={'Withdraw'}
            />
            <ConfirmationDialog
              onConfirm={onWithdrawBooking}
              onReject={onCloseWithdrawConfirmation}
              open={withdrawDialogOpened}
              title={'Confirm withdrawal request'}
              description={'Are you sure you want to withdraw this holiday request?'}
              confirmText={'Confirm'}
            />
          </Grid>
        ) : holiday.approvalStatusNumber === BookingStatus.Approved &&
          new Date(holiday.startDate) > new Date() ? (
          <OfflineButton
            variant="contained"
            color="primary"
            onClick={openCancellationDialog}
            data-bookingid={holiday.bookingId}
            text={'Cancel'}
          />
        ) : holiday.approvalStatusNumber === BookingStatus.PendingCancellation &&
          holiday.isManagerApprovalRequired ? (
          <OfflineButton
            className={
              !holiday.canSendCancellationApprovalRequestReminder ? 'Mui-disabled' : ''
            }
            variant="contained"
            color="primary"
            onClick={handleSendCancellationReminderClick}
            data-bookingid={holiday.bookingId}
            text={
              holiday.canSendCancellationApprovalRequestReminder
                ? resendRequestText
                : requestSentText
            }
          />
        ) : (
          <></>
        )}
      </Box>
      {showCanNotSendReminder ? (
        <Alert
          severity="info"
          onClose={closeCanNotSendReminder}
          sx={{ marginTop: '0.5rem' }}
        >
          {buttonAvailabilityText}
        </Alert>
      ) : null}
      <ConfirmationDialog
        onConfirm={onConfirmCancellation}
        onReject={onRejectCancellation}
        open={cancellationDialogOpened}
        title={'Confirm cancellation request'}
        description={'Are you sure you want to cancel this holiday?'}
        confirmText={'Confirm'}
      />
      <ConfirmationDialog
        onConfirm={onConfirmResendHolidayRequest}
        onReject={onRejectResendHolidayRequest}
        open={resendHolidayRequestDialogOpened}
        title={reminderConfirmationDialogTitle}
        description={
          'Are you sure you want to remind your manager about this absence request?'
        }
        confirmText={'Confirm'}
      />
      <ConfirmationDialog
        onConfirm={onConfirmResendCancellationRequest}
        onReject={onRejectResendCancellationRequest}
        open={resendCancellationRequestDialogOpened}
        title={reminderConfirmationDialogTitle}
        description={
          'Are you sure you want to remind your manager about this absence cancellation?'
        }
        confirmText={'Confirm'}
      />
    </Box>
  );
};
